import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, Skeleton, alpha, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress,
} from '@mui/material';
import {
  Visibility as WatchIcon,
  WarningAmber as WarnIcon,
  CheckCircleOutline as SafeIcon,
} from '@mui/icons-material';
import { legendaryAPI } from '../../services/api';
import { useTranslation } from 'react-i18next';
import { getScoreColor } from '../../theme';

const STRATEGY_COLORS = {
  ONEIL: '#3b82f6',
  MINERVINI: '#8b5cf6',
  WEINSTEIN: '#10b981',
  TURTLE: '#f97316',
  LIVERMORE: '#ef4444',
};

const STRATEGY_LABELS = {
  ONEIL: "O'Neil",
  MINERVINI: 'Minervini',
  WEINSTEIN: 'Weinstein',
  TURTLE: 'Turtle',
  LIVERMORE: 'Livermore',
};

const getDistColor = (d) => {
  if (d === null || d === undefined) return '#94a3b8';
  if (d <= 1) return '#10b981';
  if (d <= 3) return '#3b82f6';
  if (d <= 5) return '#f59e0b';
  return '#94a3b8';
};

const getTrapColor = (t) => {
  if (t <= 10) return '#10b981';
  if (t <= 25) return '#3b82f6';
  if (t <= 40) return '#f59e0b';
  return '#ef4444';
};

const getTrapLabel = (t, score) => {
  if (score <= 10) return t('legendary.watchlist.trap.safe');
  if (score <= 25) return t('legendary.watchlist.trap.low');
  if (score <= 40) return t('legendary.watchlist.trap.warn');
  return t('legendary.watchlist.trap.danger');
};

const parseTrapFlag = (flag, t) => {
  const match = flag.match(/^([a-z_]+)(\((.+)\))?$/);
  if (!match) return { key: flag, label: flag, detail: '' };
  const key = match[1];
  const detail = match[3] || '';
  const label = t(`legendary.watchlist.trap.${key}`, { defaultValue: key });
  return { key, label, detail };
};

const getRegimeColor = (r) => {
  if (r === 'Bullish') return '#10b981';
  if (r === 'Neutral') return '#f59e0b';
  return '#ef4444';
};

const REASON_COLORS = {
  'Gần pivot': '#8b5cf6',
  'Vol khô': '#3b82f6',
  'ATR% nén': '#ec4899',
  'Đóng cửa mạnh': '#f59e0b',
  'RS top': '#10b981',
  'Trend template': '#6366f1',
  'Gần đỉnh 52w': '#14b8a6',
  'VCP nén': '#8b5cf6',
  'Vol khô dần': '#3b82f6',
  'Stage 2': '#10b981',
  'Pullback': '#3b82f6',
  'Áp lực bán': '#f59e0b',
  'Giữ đáy': '#10b981',
  'Gần đỉnh kênh': '#f97316',
  'Sắp phá': '#ef4444',
  'ATR ổn': '#14b8a6',
  'Trên SMA': '#10b981',
  'Uptrend': '#10b981',
  'Sát pivotal': '#8b5cf6',
  'Nén range': '#ec4899',
  'Absorption': '#f59e0b',
};

function getReasonColor(reason) {
  for (const [key, color] of Object.entries(REASON_COLORS)) {
    if (reason.toLowerCase().includes(key.toLowerCase())) return color;
  }
  return '#64748b';
}

// ─── Market Regime Banner ───
function RegimeBanner({ breadth, regime, scanTime, t }) {
  const color = getRegimeColor(regime);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 2.5, py: 1.5,
      borderRadius: 2, bgcolor: alpha(color, 0.04), border: `1px solid ${alpha(color, 0.12)}`,
    }}>
      <Box sx={{
        width: 8, height: 8, borderRadius: '50%', bgcolor: color,
        boxShadow: `0 0 6px ${alpha(color, 0.4)}`,
      }} />
      <Typography variant="body2" fontWeight={700} sx={{ color }}>
        {t('legendary.watchlist.regime.market')}: {regime}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {t('legendary.watchlist.regime.breadth', { value: breadth })}
      </Typography>
      <Box sx={{ ml: 'auto' }}>
        <Typography variant="caption" color="text.secondary">
          {t('legendary.watchlist.regime.scanTime', { value: scanTime })}
        </Typography>
      </Box>
    </Box>
  );
}

// ─── Strategy Filter Strip ───
function StrategyFilterStrip({ results, selectedStrategy, onSelect, t }) {
  const strategies = Object.keys(STRATEGY_LABELS);
  const total = Object.values(results).reduce((sum, arr) => sum + arr.length, 0);
  const allActive = selectedStrategy === 'ALL';
  const allColor = '#667eea';

  const renderChip = (key, label, count, color, active) => (
    <Box
      key={key}
      onClick={() => onSelect(key)}
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: 0.6, cursor: 'pointer',
        px: 1.5, py: 0.6, borderRadius: 1.5,
        border: '1px solid',
        borderColor: active ? color : count > 0 ? alpha(color, 0.25) : 'divider',
        bgcolor: active ? alpha(color, 0.1) : 'transparent',
        transition: 'all 0.2s',
        '&:hover': { bgcolor: alpha(color, 0.08), borderColor: alpha(color, 0.4) },
      }}
    >
      <Box sx={{
        width: 6, height: 6, borderRadius: '50%',
        bgcolor: active || count > 0 ? color : alpha('#94a3b8', 0.3),
        boxShadow: active ? `0 0 6px ${alpha(color, 0.4)}` : 'none',
      }} />
      <Typography sx={{
        fontSize: '0.78rem', fontWeight: active ? 700 : 600,
        color: active ? color : count > 0 ? 'text.primary' : 'text.disabled',
      }}>
        {label}
      </Typography>
      <Box sx={{
        minWidth: 20, height: 20, borderRadius: '10px', display: 'inline-flex',
        alignItems: 'center', justifyContent: 'center',
        fontSize: '0.72rem', fontWeight: 800, px: 0.5,
        bgcolor: count > 0 ? alpha(color, active ? 0.18 : 0.1) : alpha('#94a3b8', 0.06),
        color: count > 0 ? color : '#94a3b8',
      }}>
        {count}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', gap: 0.8, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      {renderChip('ALL', t('legendary.watchlist.allStrategies'), total, allColor, allActive)}
      {strategies.map(s =>
        renderChip(s, STRATEGY_LABELS[s], (results[s] || []).length, STRATEGY_COLORS[s], selectedStrategy === s)
      )}
    </Box>
  );
}

// ─── Watchlist Table ───
function WatchlistTable({ items, showStrategy, t }) {
  if (!items.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <WatchIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {t('legendary.watchlist.noSetup')}
        </Typography>
      </Box>
    );
  }

  return (
    <Table size="small" sx={{ minWidth: 1400, '& th': { fontWeight: 700, fontSize: '0.7rem', color: '#64748b', borderColor: '#f1f5f9', py: 1, whiteSpace: 'nowrap' }, '& td': { whiteSpace: 'nowrap' } }}>
      <TableHead>
        <TableRow sx={{ bgcolor: '#fafbfc' }}>
          <TableCell sx={{ width: 32 }}>{t('legendary.watchlist.columns.rank')}</TableCell>
          <TableCell>{t('legendary.watchlist.columns.symbol')}</TableCell>
          {showStrategy && <TableCell>{t('legendary.watchlist.columns.strategy')}</TableCell>}
          <TableCell align="center">{t('legendary.watchlist.columns.score')}</TableCell>
          <TableCell align="right">{t('legendary.watchlist.columns.pivotEst')}</TableCell>
          <TableCell align="right">{t('legendary.watchlist.columns.currentPrice')}</TableCell>
          <TableCell align="center">{t('legendary.watchlist.columns.distPivot')}</TableCell>
          <TableCell align="center">{t('legendary.watchlist.columns.daysInSetup')}</TableCell>
          <TableCell align="center">{t('legendary.watchlist.columns.risk')}</TableCell>
          <TableCell align="center">{t('legendary.watchlist.columns.trap')}</TableCell>
          <TableCell align="center">{t('legendary.watchlist.columns.rs')}</TableCell>
          <TableCell align="right">{t('legendary.watchlist.columns.stop')}</TableCell>
          <TableCell align="right">{t('legendary.watchlist.columns.target')}</TableCell>
          <TableCell align="center">{t('legendary.watchlist.columns.rr')}</TableCell>
          <TableCell align="center">{t('legendary.watchlist.columns.riskPct')}</TableCell>
          <TableCell>{t('legendary.watchlist.columns.reasons')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {items.map((item, idx) => {
          const scoreColor = getScoreColor(item.setup_score_level);
          const distColor = getDistColor(item.distance_pct);
          const stratColor = STRATEGY_COLORS[item.method] || '#64748b';
          const price = item.meta?.price;

          return (
            <TableRow key={`${item.symbol}-${item.method}-${idx}`} sx={{
              bgcolor: idx % 2 === 0 ? '#fff' : '#fafbfc',
              '&:hover': { bgcolor: alpha('#667eea', 0.03) },
              '& td': { py: 0.8, fontSize: '0.78rem', borderColor: '#f1f5f9' },
            }}>
              {/* Rank */}
              <TableCell>
                <Box sx={{
                  width: 22, height: 22, borderRadius: '50%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  bgcolor: idx < 3 ? alpha(scoreColor, 0.1) : 'transparent',
                  color: idx < 3 ? scoreColor : '#94a3b8',
                  fontWeight: 700, fontSize: '0.7rem',
                }}>
                  {idx + 1}
                </Box>
              </TableCell>

              {/* Symbol */}
              <TableCell>
                <Typography variant="body2" fontWeight={800} sx={{ fontFamily: 'monospace', color: '#1e293b' }}>
                  {item.symbol}
                </Typography>
              </TableCell>

              {/* Strategy */}
              {showStrategy && (
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: stratColor, flexShrink: 0 }} />
                    <Typography variant="caption" fontWeight={600} sx={{ color: stratColor, fontSize: '0.72rem' }}>
                      {STRATEGY_LABELS[item.method] || item.method}
                    </Typography>
                  </Box>
                </TableCell>
              )}

              {/* Score */}
              <TableCell align="center">
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                  <Typography variant="body2" fontWeight={800} sx={{ color: scoreColor, fontFamily: 'monospace', fontSize: '0.82rem' }}>
                    {item.setup_score}
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, item.setup_score)}
                    sx={{
                      width: 28, height: 3, borderRadius: 1,
                      bgcolor: alpha(scoreColor, 0.12),
                      '& .MuiLinearProgress-bar': { bgcolor: scoreColor, borderRadius: 1 },
                    }}
                  />
                </Box>
              </TableCell>

              {/* Pivot est */}
              <TableCell align="right">
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', color: '#475569' }}>
                  {item.pivot_est ? item.pivot_est.toLocaleString('vi-VN', { maximumFractionDigits: 1 }) : '—'}
                </Typography>
              </TableCell>

              {/* Current price */}
              <TableCell align="right">
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', color: '#1e293b' }}>
                  {price ? price.toLocaleString('vi-VN', { maximumFractionDigits: 1 }) : '—'}
                </Typography>
              </TableCell>

              {/* Distance to pivot */}
              <TableCell align="center">
                {item.distance_pct !== null && item.distance_pct !== undefined ? (
                  <Typography variant="body2" fontWeight={700} sx={{ color: distColor, fontFamily: 'monospace', fontSize: '0.78rem' }}>
                    {item.distance_pct > 0 ? '-' : '+'}{Math.abs(item.distance_pct).toFixed(1)}%
                  </Typography>
                ) : '—'}
              </TableCell>

              {/* Days in setup */}
              <TableCell align="center">
                <Typography variant="body2" fontWeight={600} sx={{
                  color: item.days_in_setup > 20 ? '#f59e0b' : '#64748b',
                  fontFamily: 'monospace',
                }}>
                  {item.days_in_setup || '—'}
                </Typography>
              </TableCell>

              {/* Risk hint (ATR%) */}
              <TableCell align="center">
                {item.risk_hint ? (
                  <Typography variant="body2" fontWeight={600} sx={{
                    fontFamily: 'monospace', fontSize: '0.75rem',
                    color: item.risk_hint <= 3 ? '#10b981' : item.risk_hint <= 5 ? '#f59e0b' : '#ef4444',
                  }}>
                    {item.risk_hint}%
                  </Typography>
                ) : '—'}
              </TableCell>

              {/* Trap score */}
              <TableCell align="center">
                {(() => {
                  const ts = item.trap_score || 0;
                  const tc = getTrapColor(ts);
                  const tl = getTrapLabel(t, ts);
                  const flags = (item.trap_flags || []).map(f => parseTrapFlag(f, t));
                  const hasFlags = flags.length > 0;

                  const content = (
                    <Box sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.4,
                      cursor: hasFlags ? 'help' : 'default',
                    }}>
                      {ts > 40 ? (
                        <WarnIcon sx={{ fontSize: 12, color: tc }} />
                      ) : ts <= 10 ? (
                        <SafeIcon sx={{ fontSize: 12, color: tc }} />
                      ) : (
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: tc,
                          boxShadow: ts > 25 ? `0 0 4px ${alpha(tc, 0.5)}` : 'none',
                        }} />
                      )}
                      <Typography variant="body2" fontWeight={700} sx={{
                        fontFamily: 'monospace', fontSize: '0.7rem', color: tc, lineHeight: 1,
                      }}>
                        {tl}
                      </Typography>
                    </Box>
                  );

                  if (!hasFlags) return content;

                  return (
                    <Tooltip
                      arrow
                      placement="left"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#1a1a2e',
                            border: `1px solid ${alpha(tc, 0.3)}`,
                            borderRadius: 1.5,
                            p: 1.2,
                            maxWidth: 260,
                          }
                        },
                        arrow: { sx: { color: '#1a1a2e' } },
                      }}
                      title={
                        <Box>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: tc, mb: 0.5 }}>
                            {t('legendary.watchlist.trap.scoreLabel', { score: ts })}
                          </Typography>
                          {flags.map((f, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                              <Typography sx={{ fontSize: '0.7rem', color: '#e2e8f0' }}>
                                {f.label}
                              </Typography>
                              {f.detail && (
                                <Typography sx={{ fontSize: '0.65rem', color: alpha('#e2e8f0', 0.5), fontFamily: 'monospace' }}>
                                  {f.detail}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      }
                    >
                      {content}
                    </Tooltip>
                  );
                })()}
              </TableCell>

              {/* RS percentile */}
              <TableCell align="center">
                {item.rs_percentile ? (
                  <Typography variant="body2" fontWeight={700} sx={{
                    fontFamily: 'monospace', fontSize: '0.75rem',
                    color: item.rs_percentile >= 70 ? '#10b981' : item.rs_percentile >= 50 ? '#3b82f6' : '#94a3b8',
                  }}>
                    {Math.round(item.rs_percentile)}
                  </Typography>
                ) : '—'}
              </TableCell>

              {/* Stop price */}
              <TableCell align="right">
                {item.stop_price ? (
                  <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#ef4444' }}>
                    {item.stop_price.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}
                  </Typography>
                ) : '—'}
              </TableCell>

              {/* Target est */}
              <TableCell align="right">
                {item.target_est ? (
                  <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#10b981' }}>
                    {item.target_est.toLocaleString('vi-VN', { maximumFractionDigits: 1 })}
                  </Typography>
                ) : '—'}
              </TableCell>

              {/* R:R ratio */}
              <TableCell align="center">
                {item.rr_ratio ? (
                  <Typography variant="body2" fontWeight={700} sx={{
                    fontFamily: 'monospace', fontSize: '0.75rem',
                    color: item.rr_ratio >= 2.5 ? '#10b981' : item.rr_ratio >= 2.0 ? '#3b82f6' : '#f59e0b',
                  }}>
                    {item.rr_ratio}:1
                  </Typography>
                ) : '—'}
              </TableCell>

              {/* Risk % */}
              <TableCell align="center">
                {item.risk_pct != null ? (
                  <Typography variant="body2" fontWeight={600} sx={{
                    fontFamily: 'monospace', fontSize: '0.75rem',
                    color: item.risk_pct <= 5 ? '#10b981' : item.risk_pct <= 8 ? '#f59e0b' : '#ef4444',
                  }}>
                    {item.risk_pct}%
                  </Typography>
                ) : '—'}
              </TableCell>

              {/* Reasons — show all */}
              <TableCell>
                <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: 0.5 }}>
                  {item.reasons.map((r, ri) => (
                    <React.Fragment key={ri}>
                      {ri > 0 && (
                        <Typography component="span" sx={{ color: '#d1d5db', fontSize: '0.65rem', lineHeight: 1, flexShrink: 0 }}>·</Typography>
                      )}
                      <Typography component="span" sx={{
                        fontSize: '0.7rem', fontWeight: 600,
                        color: getReasonColor(r), lineHeight: 1.3,
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        {r}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

// ─── Loading skeleton ───
function WatchlistSkeleton() {
  const strategyColors = ['#667eea', '#3b82f6', '#8b5cf6', '#10b981', '#f97316', '#ef4444'];
  return (
    <Box sx={{ width: '100%' }}>
      {/* ── Regime banner skeleton — matches RegimeBanner layout ── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, mb: 2, px: 2.5, py: 1.5,
        borderRadius: 2, bgcolor: alpha('#94a3b8', 0.04), border: `1px solid ${alpha('#94a3b8', 0.12)}`,
      }}>
        {/* Dot */}
        <Skeleton variant="circular" width={8} height={8} />
        {/* "Thị trường: Bullish" */}
        <Skeleton width={130} height={18} sx={{ borderRadius: 0.5 }} />
        {/* "Độ rộng: xx% mã trên MA50" */}
        <Skeleton width={190} height={14} sx={{ borderRadius: 0.5 }} />
        {/* "Quét: 0s" */}
        <Box sx={{ ml: 'auto' }}>
          <Skeleton width={60} height={14} sx={{ borderRadius: 0.5 }} />
        </Box>
      </Box>

      {/* ── Strategy filter strip skeleton ── */}
      <Box sx={{ display: 'flex', gap: 0.8, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        {strategyColors.map((color, i) => (
          <Box key={i} sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.6, borderRadius: 1.5,
            border: '1px solid', borderColor: i === 0 ? alpha(color, 0.35) : alpha('#94a3b8', 0.15),
            bgcolor: i === 0 ? alpha(color, 0.1) : 'transparent',
          }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: alpha(color, 0.3) }} />
            <Skeleton width={i === 0 ? 36 : 52} height={14} sx={{ borderRadius: 0.5 }} animation="wave" />
            <Skeleton width={20} height={18} sx={{ borderRadius: '10px' }} animation="wave" />
          </Box>
        ))}
      </Box>

      {/* ── Table skeleton — matches real Card + WatchlistTable ── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, width: '100%', overflow: 'hidden' }}>
        {/* Table header bar — matches the real sticky header */}
        <Box sx={{
          px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #f1f5f9',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="circular" width={18} height={18} />
            <Skeleton width={80} height={16} sx={{ borderRadius: 0.5 }} />
            <Skeleton width={50} height={13} sx={{ borderRadius: 0.5 }} />
          </Box>
          <Skeleton width={120} height={13} sx={{ borderRadius: 0.5 }} />
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{
            width: '100%', tableLayout: 'fixed',
            '& th': { fontWeight: 700, fontSize: '0.7rem', color: '#64748b', borderColor: '#f1f5f9', py: 1, whiteSpace: 'nowrap' },
          }}>
            {/* Column widths matching the real table proportions */}
            <colgroup>
              <col style={{ width: '3.5%' }} />   {/* # */}
              <col style={{ width: '5.5%' }} />   {/* Mã CK */}
              <col style={{ width: '8%' }} />     {/* Strategy */}
              <col style={{ width: '6%' }} />     {/* Score */}
              <col style={{ width: '7%' }} />     {/* Strength */}
              <col style={{ width: '7%' }} />     {/* Entry */}
              <col style={{ width: '7%' }} />     {/* Stop */}
              <col style={{ width: '7%' }} />     {/* Target */}
              <col style={{ width: '6%' }} />     {/* Risk% */}
              <col style={{ width: '5%' }} />     {/* R:R */}
              <col style={{ width: '5%' }} />     {/* Trap */}
              <col style={{ width: '4%' }} />     {/* RS */}
              <col style={{ width: '7%' }} />     {/* Pivot Est */}
              <col style={{ width: '5%' }} />     {/* Cách pivot */}
              <col style={{ width: '4%' }} />     {/* Ngày nén */}
              <col style={{ width: '13%' }} />    {/* Chi tiết */}
            </colgroup>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafbfc' }}>
                <TableCell><Skeleton width="40%" height={11} sx={{ borderRadius: 0.5 }} /></TableCell>
                <TableCell><Skeleton width="60%" height={11} sx={{ borderRadius: 0.5 }} /></TableCell>
                <TableCell><Skeleton width="75%" height={11} sx={{ borderRadius: 0.5 }} /></TableCell>
                <TableCell align="center"><Skeleton width="60%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton width="70%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton width="60%" height={11} sx={{ borderRadius: 0.5, ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton width="55%" height={11} sx={{ borderRadius: 0.5, ml: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton width="60%" height={11} sx={{ borderRadius: 0.5, ml: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton width="65%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton width="50%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton width="55%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton width="45%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell align="right"><Skeleton width="65%" height={11} sx={{ borderRadius: 0.5, ml: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton width="70%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell align="center"><Skeleton width="60%" height={11} sx={{ borderRadius: 0.5, mx: 'auto' }} /></TableCell>
                <TableCell><Skeleton width="45%" height={11} sx={{ borderRadius: 0.5 }} /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(10)].map((_, row) => {
                const fakeColor = strategyColors[1 + (row % 5)];
                return (
                  <TableRow key={row} sx={{
                    bgcolor: row % 2 === 0 ? '#fff' : '#fafbfc',
                    '& td': { py: 0.8, fontSize: '0.78rem', borderColor: '#f1f5f9' },
                  }}>
                    {/* # Rank */}
                    <TableCell>
                      <Box sx={{
                        width: 22, height: 22, borderRadius: '50%', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        bgcolor: row < 3 ? alpha('#10b981', 0.1) : 'transparent',
                      }}>
                        <Skeleton variant="circular" width={14} height={14} animation="wave" />
                      </Box>
                    </TableCell>
                    {/* Mã CK */}
                    <TableCell>
                      <Skeleton width="70%" height={14} sx={{ borderRadius: 0.5 }} animation="wave" />
                    </TableCell>
                    {/* Chiến lược — dot + name */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: alpha(fakeColor, 0.3), flexShrink: 0 }} />
                        <Skeleton width="65%" height={13} sx={{ borderRadius: 0.5 }} animation="wave" />
                      </Box>
                    </TableCell>
                    {/* Score + progress bar */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Skeleton width={24} height={14} sx={{ borderRadius: 0.5 }} animation="wave" />
                        <Box sx={{ width: 28, height: 3, borderRadius: 1, bgcolor: alpha('#94a3b8', 0.12), overflow: 'hidden' }}>
                          <Box sx={{ width: `${50 + row * 4}%`, height: '100%', borderRadius: 1, bgcolor: alpha('#94a3b8', 0.2) }} />
                        </Box>
                      </Box>
                    </TableCell>
                    {/* Strength */}
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: alpha('#10b981', 0.3) }} />
                        <Skeleton width="55%" height={13} sx={{ borderRadius: 0.5 }} animation="wave" />
                      </Box>
                    </TableCell>
                    {/* Entry */}
                    <TableCell align="right">
                      <Skeleton width="70%" height={14} sx={{ borderRadius: 0.5, ml: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Stop */}
                    <TableCell align="right">
                      <Skeleton width="70%" height={14} sx={{ borderRadius: 0.5, ml: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Target */}
                    <TableCell align="right">
                      <Skeleton width="70%" height={14} sx={{ borderRadius: 0.5, ml: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Risk% */}
                    <TableCell align="center">
                      <Skeleton width="55%" height={14} sx={{ borderRadius: 0.5, mx: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* R:R */}
                    <TableCell align="center">
                      <Skeleton width="50%" height={14} sx={{ borderRadius: 0.5, mx: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Trap — icon + label */}
                    <TableCell align="center">
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, mx: 'auto' }}>
                        <Skeleton variant="circular" width={12} height={12} animation="wave" />
                        <Skeleton width={28} height={13} sx={{ borderRadius: 0.5 }} animation="wave" />
                      </Box>
                    </TableCell>
                    {/* RS */}
                    <TableCell align="center">
                      <Skeleton width="50%" height={14} sx={{ borderRadius: 0.5, mx: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Pivot Est */}
                    <TableCell align="right">
                      <Skeleton width="70%" height={14} sx={{ borderRadius: 0.5, ml: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Cách pivot */}
                    <TableCell align="center">
                      <Skeleton width="60%" height={14} sx={{ borderRadius: 0.5, mx: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Ngày nén */}
                    <TableCell align="center">
                      <Skeleton width="50%" height={14} sx={{ borderRadius: 0.5, mx: 'auto' }} animation="wave" />
                    </TableCell>
                    {/* Chi tiết — multiple reason tags */}
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Skeleton width="35%" height={13} sx={{ borderRadius: 0.5 }} animation="wave" />
                        <Typography component="span" sx={{ color: '#e2e8f0', fontSize: '0.65rem', lineHeight: 1 }}>·</Typography>
                        <Skeleton width="25%" height={13} sx={{ borderRadius: 0.5 }} animation="wave" />
                        <Typography component="span" sx={{ color: '#e2e8f0', fontSize: '0.65rem', lineHeight: 1 }}>·</Typography>
                        <Skeleton width="30%" height={13} sx={{ borderRadius: 0.5 }} animation="wave" />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
}

// ─── Main Component ───
export default function WatchlistPreSignal() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState('ALL');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await legendaryAPI.getWatchlist({ topN: 30, minScore: 55 });
      setData(res.data);
    } catch (err) {
      console.error('Watchlist fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allItems = useMemo(() => {
    if (!data?.results) return [];
    if (selectedStrategy === 'ALL') {
      const merged = [];
      for (const [method, items] of Object.entries(data.results)) {
        for (const item of items) {
          merged.push({ ...item, method: item.method || method });
        }
      }
      return merged.sort((a, b) => b.setup_score - a.setup_score);
    }
    return (data.results[selectedStrategy] || []).sort((a, b) => b.setup_score - a.setup_score);
  }, [data, selectedStrategy]);

  if (loading) return <WatchlistSkeleton />;
  if (!data) return null;

  return (
    <Box>
      {/* Regime Banner */}
      <RegimeBanner
        breadth={data.market_breadth}
        regime={data.regime}
        scanTime={data.scan_time_s}
        t={t}
      />

      {/* Strategy filter strip */}
      <StrategyFilterStrip
        results={data.results || {}}
        selectedStrategy={selectedStrategy}
        onSelect={setSelectedStrategy}
        t={t}
      />

      {/* Watchlist Table */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{
          px: 2, py: 1.2, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #f1f5f9', position: 'sticky', left: 0,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WatchIcon sx={{ fontSize: 18, color: '#667eea' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#334155' }}>
              {t('legendary.watchlist.title')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>
              {t('legendary.watchlist.count', { count: allItems.length })}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>
            {t('legendary.watchlist.filterDesc')}
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <WatchlistTable items={allItems} showStrategy={selectedStrategy === 'ALL'} t={t} />
        </Box>
      </Card>
    </Box>
  );
}
